import {Paper, Popper, withStyles} from '@material-ui/core';
import withWidth, {WithWidth} from '@material-ui/core/withWidth';
import React, {useState} from 'react';
import unionize, {ofType, UnionOf} from 'unionize';
import './ErrorPopover.sass';

const styles = (theme: any) => ({
  root: {
    flexGrow: 1,
  },
  scrollContainer: {
    height: 400,
    overflow: 'auto',
    marginBottom: theme.spacing(3),
  },
  scroll: {
    position: 'relative',
    width: '230%',
    backgroundColor: theme.palette.background.paper,
    height: '230%',
  },
  legend: {
    marginTop: theme.spacing(2),
    maxWidth: 300,
  },
  paper: {
    maxWidth: 400,
    overflow: 'auto',
    padding: '1rem',
  },
  select: {
    width: 200,
  },
  popper: {
    zIndex: 2,
    '&[x-placement*="bottom"] $arrow': {
      top: 0,
      left: 0,
      marginTop: '-0.9em',
      width: '3em',
      height: '1em',
      '&::before': {
        borderWidth: '0 1em 1em 1em',
        borderColor: `transparent transparent ${theme.palette.common.white} transparent`,
      },
    },
    '&[x-placement*="top"] $arrow': {
      bottom: 0,
      left: 0,
      marginBottom: '-0.9em',
      width: '3em',
      height: '1em',
      '&::before': {
        borderWidth: '1em 1em 0 1em',
        borderColor: `${theme.palette.common.white} transparent transparent transparent`,
      },
    },
    '&[x-placement*="right"] $arrow': {
      left: 0,
      marginLeft: '-0.9em',
      height: '3em',
      width: '1em',
      '&::before': {
        borderWidth: '1em 1em 1em 0',
        borderColor: `transparent ${theme.palette.common.white} transparent transparent`,
      },
    },
    '&[x-placement*="left"] $arrow': {
      right: 0,
      marginRight: '-0.9em',
      height: '3em',
      width: '1em',
      '&::before': {
        borderWidth: '1em 0 1em 1em',
        borderColor: `transparent transparent transparent ${theme.palette.common.white}`,
      },
    },
  },
  arrow: {
    position: 'absolute',
    fontSize: 7,
    width: '3em',
    height: '3em',
    '&::before': {
      content: '""',
      margin: 'auto',
      display: 'block',
      width: 0,
      height: 0,
      borderStyle: 'solid',
    },
  },
});
export type Requirement = {
  message: string;
  isMet: boolean;
};

export const showRequirementErrors = (showPasswordErrors: boolean) => (errors: ErrorsUnion) =>
  showPasswordErrors && Errors.is.requirements(errors) && errors.value.some(error => !error.isMet);

export const Errors = unionize({
  requirements: ofType<{value: Requirement[]}>(),
  standardErrors: ofType<{value: string[]}>(),
});

export const NoErrors = Errors.standardErrors({value: []});

export type ErrorsUnion = UnionOf<typeof Errors>;

export type ErrorPopoverProps = {
  anchor?: HTMLElement;
  errors: ErrorsUnion;
  classes: any;
  open: boolean;
} & WithWidth;

const ErrorPopover = (props: ErrorPopoverProps) => {
  const {classes} = props;
  const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null);

  return (
    <Popper
      open={props.open}
      anchorEl={props.anchor}
      placement={'bottom'}
      disablePortal
      className={classes.popper}
      modifiers={{
        arrow: {
          element: arrowRef,
          enabled: true,
        },
        flip: {
          enabled: true,
        },
        preventOverflow: {
          boundariesElement: 'scrollParent',
          enabled: true,
        },
      }}
    >
      <span className={classes.arrow} ref={setArrowRef} />
      <Paper className={classes.paper}>
        <span>Requirements:</span>
        {Errors.match(props.errors, {
          requirements: requirements => (
            <ul className="error-popover__requirement-list">
              {requirements.value.map((requirement, index) => (
                <li key={`${index}-error_popover`}>
                  {requirement.isMet ? (
                    <i className="error-popover__success-icon fa fa-check" />
                  ) : (
                    <i className="error-popover__error-icon fa fa-exclamation" />
                  )}
                  <span>{requirement.message}</span>
                </li>
              ))}
            </ul>
          ),
          standardErrors: errors => (
            <ul className="error-popover__requirement-list">
              {errors.value.map((error, index) => (
                <li key={`${index}-error_popover`}>
                  <i className="error-popover__error-icon fa fa-exclamation" />
                  <span>{error}</span>
                </li>
              ))}
            </ul>
          ),
        })}
      </Paper>
    </Popper>
  );
};

export default withWidth()(withStyles(styles as any)(ErrorPopover));
